import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Scatter } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartDataset,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { isEqual } from "lodash";
import { numericalString } from "../../../types";
import { PitLoss } from "../PitBoostCorrectedLeaderboard/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

interface PitLossPoint {
  x: number;
  y: number;
  pitLoss: PitLoss;
}

function PitLossScatter() {
  const pitLosses = useSelector(
    (state: RootState) => state.virtualGaps.data.pitLosses,
    isEqual,
  );

  const entry = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.entry,
    isEqual,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  // Get second to last loop name
  const secondToLastLoopName = useMemo(() => {
    if (!timingLoops) return "Gap (s)";
    const keys = Object.keys(timingLoops);
    if (keys.length < 2) return "Gap (s)";
    const secondToLastKey = keys[keys.length - 2];
    const loopInfo = timingLoops[secondToLastKey as `${number}`];
    return `Gap at ${loopInfo?.name || secondToLastKey} (s)`;
  }, [timingLoops]);

  const getDriverName = (participant: numericalString) => {
    return entry?.[participant]?.drivers?.["1"]?.shortName || participant;
  };

  const chartData = useMemo(() => {
    if (!pitLosses || pitLosses.length === 0) {
      return {
        datasets: [],
      };
    }

    // Categorize pit losses into 4 groups with full pit loss data
    const inAttackKeptPosition: PitLossPoint[] = [];
    const notInAttackKeptPosition: PitLossPoint[] = [];
    const inAttackLostPosition: PitLossPoint[] = [];
    const notInAttackLostPosition: PitLossPoint[] = [];

    for (const pitLoss of pitLosses) {
      const point: PitLossPoint = { x: pitLoss.lap, y: pitLoss.gap, pitLoss };

      if (pitLoss.behindInAttack && pitLoss.keptPosition) {
        inAttackKeptPosition.push(point);
      } else if (!pitLoss.behindInAttack && pitLoss.keptPosition) {
        notInAttackKeptPosition.push(point);
      } else if (pitLoss.behindInAttack && !pitLoss.keptPosition) {
        inAttackLostPosition.push(point);
      } else {
        notInAttackLostPosition.push(point);
      }
    }

    const datasets: ChartDataset<"scatter", PitLossPoint[]>[] = [
      {
        label: "Kept position (Behind in Attack)",
        data: inAttackKeptPosition,
        backgroundColor: "#22c55e", // green
        borderColor: "#22c55e",
        pointRadius: 7,
        pointHoverRadius: 7,
        pointBorderWidth: 4,
        pointHoverBorderWidth: 4,
        pointStyle: "crossRot",
        showLine: false,
      },
      {
        label: "Kept position",
        data: notInAttackKeptPosition,
        backgroundColor: "#22c55e", // green
        borderColor: "#22c55e",
        pointRadius: 5,
        pointHoverRadius: 5,
        pointStyle: "circle",
        showLine: false,
      },
      {
        label: "Lost position (Behind in Attack)",
        data: inAttackLostPosition,
        backgroundColor: "#ef4444", // red
        borderColor: "#ef4444",
        pointRadius: 7,
        pointHoverRadius: 7,
        pointBorderWidth: 4,
        pointHoverBorderWidth: 4,
        pointStyle: "crossRot",
        showLine: false,
      },
      {
        label: "Lost position",
        data: notInAttackLostPosition,
        backgroundColor: "#ef4444", // red
        pointRadius: 5,
        pointHoverRadius: 5,
        pointStyle: "circle",
        showLine: false,
      },
    ];

    return {
      datasets,
    };
  }, [pitLosses]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#ffffff",
          usePointStyle: true,
        },
        onClick: () => {
          // Do nothing
        },
      },
      title: {
        display: true,
        text: "Pit Loss",
        color: "#ffffff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
      tooltip: {
        callbacks: {
          title: () => "",
          label: (context: { raw: unknown }) => {
            const point = context.raw as PitLossPoint;
            if (!point?.pitLoss) return "";
            const { pitLoss } = point;
            const driverInFront = getDriverName(pitLoss.participant);
            const driverBehind = getDriverName(pitLoss.participantBehind);
            const attackStatus = pitLoss.behindInAttack ? "Yes" : "No";
            const result = pitLoss.keptPosition
              ? "Kept Position"
              : "Lost Position";
            return [
              `Lap: ${pitLoss.lap}`,
              `Driver Pitting: ${driverInFront}`,
              `Driver Behind: ${driverBehind}`,
              `Behind In Attack: ${attackStatus}`,
              `Gap: ${pitLoss.gap.toFixed(2)}s`,
              `Result: ${result}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        title: {
          display: true,
          text: "Lap",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
      },
      y: {
        title: {
          display: true,
          text: secondToLastLoopName,
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
      },
    },
    interaction: {
      mode: "point" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  return (
    <div className={styles.container}>
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
}

export default PitLossScatter;
